// UTILS
import { defineStore } from "pinia"
import { reactive, ref, computed } from "vue"
import { getFilenameForAudio } from "@/helpers/getFilenameForAudio"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import { getAudioFileFromDB } from "@/services/db"

// MACHINES
import { globalActor } from "@/machines/globalMachine"
import { shapeActor } from "@/features/shape/machines/shapeMachine"
import { swipeActor } from "@/features/swipe/machines/swipeMachine"
import { driveActor } from "@/features/drive/machines/driveMachine"
import { brainActor } from "@/features/brain/machines/brainMachine"
import { moveActor } from "@/features/move/machines/moveMachine"
import { moveBisActor } from "@/features/movebis/machines/moveBisMachine"
import { voiceActor } from "@/features/voice/machines/voiceMachine"
import { screeningActor } from "@/features/screening/machines/screeningMachine"

// STORES
import { useUserStore } from "@/stores/user"

export const useHelpStore = defineStore("help", () => {
  const userStore = useUserStore()
  const assessmentSlug = computed(() => userStore.nextAssessmentName)

  const isReady = ref(false)
  const hideAudioBox = ref(false)
  const isActiveAudioBox = ref(false)
  const isActiveAudioModal = ref(false)
  const isAudioHelpAccepted = ref(false)
  const showSeeAnswerButton = ref(false)
  const isVolumeActive = ref(false)
  const state = ref("")
  const name = ref("")
  const swipeTutorialFirstTime = ref(true)
  const activeTutorial = ref(0)
  const activeTrial = ref(0)
  const currentQuestion = ref<any>(null) // Set into the assessment start view (ie Drive.vue, Shape.vue..)
  const indexOfCurrentItemBeingPlayed = ref<null | number>(null)
  const audioElement = ref(new Audio())
  const isPlaying = ref(false)
  const needOptions = ref<boolean>(false)
  const audioOptions = ref<string | undefined>(undefined)
  const showPrivateSafariTooltip = ref(false)
  const isFinalScreen = ref(false)
  const isFinalWithPrevAnswerScreen = ref(false)
  const canAssessmentBePassed = ref({
    swipe: true,
    drive: true,
    brain: true,
    voice: true,
    shape: true,
    move: true,
    movebis: true,
  })

  const af_workflow_uuid = ref("")
  const af_pm_uuid = ref("")
  const af_company_uuid = ref("")
  const af_retake = ref(false)

  let currentURL = ""
  let queue: any[] = []
  let resolveSequencePromise: (() => void) | null = null
  const actors = reactive({
    global: {
      actor: globalActor,
      state: globalActor.getSnapshot().value,
      name: "Global",
      stateCondition: "none",
    },
    swipe: {
      actor: swipeActor,
      state: swipeActor.getSnapshot().value,
      name: "Swipe",
      stateCondition: "swipe",
    },
    shape: {
      actor: shapeActor,
      state: shapeActor.getSnapshot().value,
      name: "Shape",
      stateCondition: "shape",
    },
    drive: {
      actor: driveActor,
      state: driveActor.getSnapshot().value,
      name: "Drive",
      stateCondition: "drive",
    },
    brain: {
      actor: brainActor,
      state: brainActor.getSnapshot().value,
      name: "Brain",
      stateCondition: "brain",
    },
    voice: {
      actor: voiceActor,
      state: voiceActor.getSnapshot().value,
      name: "Voice",
      stateCondition: "voice",
    },
    screening: {
      actor: screeningActor,
      state: screeningActor.getSnapshot().value,
      name: "Screening",
      stateCondition: "screening",
    },
    move: {
      actor: moveActor,
      state: moveActor.getSnapshot().value,
      name: "Move",
      stateCondition: "move",
    },
    movebis: {
      actor: moveBisActor,
      state: moveBisActor.getSnapshot().value,
      name: "MoveBis",
      stateCondition: "movebis",
    },
  })

  for (const actorObj of Object.values(actors)) {
    // @ts-ignore-next-line
    actorObj.actor.subscribe((snapshot) => {
      actorObj.state = snapshot.value
      state.value = actorObj.state
      name.value = actorObj.name

      updateIsActive()
    })
  }

  function toggleAudio(bool: boolean) {
    useUserStore().setAudio(bool)
    isAudioHelpAccepted.value = bool
    MixpanelInstance.track(
      `assessment-audio_${bool ? "activated" : "deactivated"}`,
      { assessment: name.value }
    )
  }

  function setCurrentQuestion(question) {
    currentQuestion.value = question
  }

  function setCurrentItemBeingPlayed(fileIndex) {
    indexOfCurrentItemBeingPlayed.value = fileIndex
  }

  function clearCurrentItemBeingPlayed() {
    indexOfCurrentItemBeingPlayed.value = null
  }

  function setIsReady(state) {
    isReady.value = state
  }

  function setAudioBoxHidden(state) {
    hideAudioBox.value = state
  }

  function setSwipeTutorialFirstTime(value) {
    canAssessmentBePassed.value.swipe = false
    swipeTutorialFirstTime.value = value
  }

  function getIsAssessmentNotStarted() {
    if (
      !assessmentSlug.value ||
      assessmentSlug.value === "none" ||
      assessmentSlug.value === "screening"
    ) {
      return
    }

    let assessmentSummary

    if (assessmentSlug.value === "voice") {
      // Get voice UUID
      const voiceUUID = useUserStore().getVoiceUuid

      // pick the good voice in assessment summary
      assessmentSummary =
        useUserStore().assessmentSummary?.assessments?.voices?.find(
          (voice) => voice.voice_uuid === voiceUUID
        )
    } else {
      assessmentSummary =
        useUserStore()?.assessmentSummary?.assessments[
          assessmentSlug.value.toLowerCase()
        ]
    }

    const isAssessmentTodo =
      assessmentSummary?.status === "todo" || assessmentSummary?.status === null

    return isAssessmentTodo && canAssessmentBePassed.value[assessmentSlug.value]
  }

  function updateIsActive() {
    needOptions.value = name.value === "Brain" && state.value === "practice"
    audioOptions.value = useUserStore().getShortGender
    isActiveAudioBox.value =
      name.value === "Shape" ||
      name.value === "Swipe" ||
      name.value === "Drive" ||
      name.value === "Brain" ||
      name.value === "Move" ||
      name.value === "MoveBis"

    const shouldStartAudio = isActiveAudioBox.value && isAudioHelpAccepted.value

    showSeeAnswerButton.value = name.value === "Brain"

    if (shouldStartAudio) {
      startAudioSequence()
    }

    if (name.value === "Global") {
      isActiveAudioModal.value = false
    }
  }

  function initializeAudioElement() {
    if (!audioElement.value.src) {
      audioElement.value.addEventListener("ended", () => {
        playNextInQueue()
      })
    }
  }

  function startAudioSequence(modalOptions: null | string = null) {
    isVolumeActive.value = true

    const files = getFilenameForAudio(
      name.value,
      state.value,
      activeTutorial.value,
      activeTrial.value,
      currentQuestion.value,
      modalOptions,
      needOptions.value ? audioOptions.value : undefined,
      useUserStore().getShortGender,
      swipeTutorialFirstTime.value,
      isFinalScreen.value,
      isFinalWithPrevAnswerScreen.value
    )

    if (files) {
      playSequence(files).then(() => {
        isVolumeActive.value = false
      })
    }
  }

  const playAudio = async (filename) => {
    initializeAudioElement()

    // If a URL for a previous audio file exists, revoke it to free up memory
    // This is important to prevent memory leaks by releasing URLs no longer in use
    if (currentURL) {
      URL.revokeObjectURL(currentURL)
    }

    // Get the audio file as Blob from IndexedDB
    const fileBlob = await getAudioFileFromDB(filename)
    if (!fileBlob) {
      showPrivateSafariTooltip.value = true
      return
    }

    const file = new File([fileBlob], filename, { type: "audio/mpeg" })

    currentURL = URL.createObjectURL(file)

    audioElement.value.src = currentURL

    audioElement.value.play().catch((error) => {
      console.warn("Error playing audio : " + error)
      clearCurrentItemBeingPlayed()
    })
    isPlaying.value = true
  }

  const playNextInQueue = async () => {
    if (queue.length > 0) {
      const nextFile = queue.shift()
      try {
        await playAudio(nextFile.file)
        setCurrentItemBeingPlayed(nextFile.index)
      } catch (err) {
        console.error(err)
        stopAudio()
      }
    } else {
      isPlaying.value = false
      clearCurrentItemBeingPlayed()
      if (resolveSequencePromise) {
        resolveSequencePromise()
        resolveSequencePromise = null
      }
    }
  }

  function removeAudioEventListener() {
    if (audioElement.value) {
      audioElement.value.removeEventListener("ended", playNextInQueue)
    }
  }

  function resetAudio() {
    audioElement.value = new Audio()
  }

  const playSequence = (files) => {
    return new Promise<void>((resolve) => {
      if (files.length === 0) {
        resolve()
        return
      }
      queue = files.map((file, index) => ({ index, file }))
      resolveSequencePromise = resolve
      if (!isPlaying.value) playNextInQueue()
    })
  }

  const stopAudio = () => {
    if (!isPlaying.value) return
    else {
      return new Promise<void>((resolve) => {
        audioElement.value.pause()
        audioElement.value.currentTime = 0
        queue = []
        isPlaying.value = false
        isVolumeActive.value = false
        clearCurrentItemBeingPlayed()
        if (resolveSequencePromise) {
          resolveSequencePromise()
          resolveSequencePromise = null
        }
        resolve()
      })
    }
  }

  return {
    isReady,
    hideAudioBox,
    isActiveAudioBox,
    isActiveAudioModal,
    isAudioHelpAccepted,
    showSeeAnswerButton,
    isVolumeActive,
    swipeTutorialFirstTime,
    state,
    name,
    activeTutorial,
    activeTrial,
    showPrivateSafariTooltip,
    setCurrentQuestion,
    indexOfCurrentItemBeingPlayed,
    isFinalScreen,
    isFinalWithPrevAnswerScreen,
    toggleAudio,
    isPlaying,
    setCurrentItemBeingPlayed,
    clearCurrentItemBeingPlayed,
    playSequence,
    stopAudio,
    removeAudioEventListener,
    startAudioSequence,
    resetAudio,
    setIsReady,
    setAudioBoxHidden,
    setSwipeTutorialFirstTime,
    getIsAssessmentNotStarted,
    assessmentSlug,
    canAssessmentBePassed,

    af_workflow_uuid,
    af_pm_uuid,
    af_company_uuid,
    af_retake,
  }
})
